import { Type } from "@sinclair/typebox";
import { ROLE_NONE } from "../../constants";
import { emptyChatMetrics } from "../../utils";
import { DataContentSchema } from "../data/data";
const DirectChatIdentifierSchema = Type.Object({
    kind: Type.Literal("direct_chat"),
    userId: Type.String(),
});
const GroupChatIdentifierSchema = Type.Object({
    kind: Type.Literal("group_chat"),
    groupId: Type.String(),
});
const ChannelIdentifierSchema = Type.Object({
    kind: Type.Literal("channel"),
    communityId: Type.String(),
    channelId: Type.Number(),
});
export const MultipleUserChatIdentifierSchema = Type.Union([
    ChannelIdentifierSchema,
    GroupChatIdentifierSchema,
]);
export const ChatIdentifierSchema = Type.Union([
    MultipleUserChatIdentifierSchema,
    DirectChatIdentifierSchema,
]);
const ThreadIdentifierSchema = Type.Object({
    chatId: ChatIdentifierSchema,
    threadRootMessageIndex: Type.Number(),
});
// A "default" call is a video call. An audio call stays an audio call: nobody in it can turn a
// camera on.
export const VideoCallTypeSchema = Type.Union([
    Type.Literal("broadcast"),
    Type.Literal("default"),
    Type.Literal("audio"),
]);
export function videoCallTypeFromWire(callType, audioOnly) {
    return callType === "default" && audioOnly === true ? "audio" : callType;
}
export function videoCallTypeToWire(callType) {
    return callType === "audio"
        ? { callType: "default", audioOnly: true }
        : { callType, audioOnly: false };
}
// A call that is already running keeps the type it was started with, whatever the button that
// joins it asked for
export function joinedCallType(requested, inProgress) {
    return inProgress ?? requested;
}
// An audio call is joined with the camera off whatever the user's camera setting says
export function startVideoOff(callType, cameraOn) {
    return callType === "audio" || !cameraOn;
}
export function isAttachmentContent(content) {
    switch (content.kind) {
        case "image_content":
        case "video_content":
        case "audio_content":
        case "file_content":
        case "giphy_content":
        case "crypto_content":
        case "p2p_swap_content_initial":
        case "prize_content_initial":
        case "daily_result":
            return true;
        default:
            return false;
    }
}
export function isEditableContent(kind) {
    switch (kind) {
        case "file_content":
        case "text_content":
        case "image_content":
        case "video_content":
        case "audio_content":
        case "giphy_content":
            return true;
        default:
            return false;
    }
}
export function isCaptionedContent(content) {
    switch (content.kind) {
        case "image_content":
        case "video_content":
        case "audio_content":
        case "file_content":
        case "crypto_content":
        case "giphy_content":
        case "prize_content":
        case "p2p_swap_content":
            return true;
        default:
            return false;
    }
}
export function isTransfer(content) {
    return (content.kind === "crypto_content" ||
        content.kind === "prize_content_initial" ||
        content.kind === "p2p_swap_content_initial");
}
export function canRetryMessage(content) {
    return (content.kind !== "poll_content" &&
        content.kind !== "crypto_content" &&
        content.kind !== "prize_content_initial" &&
        content.kind !== "p2p_swap_content_initial");
}
export const PlaceholderContentSchema = Type.Object({
    kind: Type.Literal("placeholder_content"),
});
export const BotPlaceholderContentSchema = Type.Object({
    kind: Type.Literal("bot_placeholder_content"),
});
export const GiphyImageSchema = Type.Object({
    height: Type.Number(),
    width: Type.Number(),
    url: Type.String(),
    mimeType: Type.String(),
});
export const GiphyContentSchema = Type.Object({
    kind: Type.Literal("giphy_content"),
    caption: Type.Optional(Type.String()),
    title: Type.String(),
    desktop: GiphyImageSchema, //will be "original" from the giphy api
    mobile: GiphyImageSchema, //will be "downsized_large" from the giphy api
});
export const MessageReportSchema = Type.Object({
    notes: Type.Optional(Type.String()),
    reasonCode: Type.Number(),
    timestamp: Type.Number(),
    reportedBy: Type.String(),
});
export const ReportedMessageContentSchema = Type.Object({
    kind: Type.Literal("reported_message_content"),
    total: Type.Number(),
    reports: Type.Array(MessageReportSchema),
});
export const MessageReminderCreatedContentSchema = Type.Object({
    kind: Type.Literal("message_reminder_created_content"),
    notes: Type.Optional(Type.String()),
    remindAt: Type.Number(),
    reminderId: Type.BigInt(),
    hidden: Type.Boolean(),
});
export const MessageReminderContentSchema = Type.Object({
    kind: Type.Literal("message_reminder_content"),
    notes: Type.Optional(Type.String()),
    reminderId: Type.BigInt(),
});
export const PrizeContentSchema = Type.Object({
    kind: Type.Literal("prize_content"),
    prizesRemaining: Type.Number(),
    prizesPending: Type.Number(),
    diamondOnly: Type.Boolean(),
    lifetimeDiamondOnly: Type.Boolean(),
    uniquePersonOnly: Type.Boolean(),
    streakOnly: Type.Number(),
    winnerCount: Type.Number(),
    userIsWinner: Type.Boolean(),
    // The token's symbol, for display. Several ledgers can share a symbol, so the token itself is
    // looked up by `ledger`.
    token: Type.String(),
    ledger: Type.String(),
    endDate: Type.BigInt(),
    caption: Type.Optional(Type.String()),
    requiresCaptcha: Type.Boolean(),
    minChitEarned: Type.Number(),
});
export var ProposalDecisionStatus;
(function (ProposalDecisionStatus) {
    ProposalDecisionStatus[ProposalDecisionStatus["Unspecified"] = 0] = "Unspecified";
    ProposalDecisionStatus[ProposalDecisionStatus["Failed"] = 1] = "Failed";
    ProposalDecisionStatus[ProposalDecisionStatus["Open"] = 2] = "Open";
    ProposalDecisionStatus[ProposalDecisionStatus["Rejected"] = 3] = "Rejected";
    ProposalDecisionStatus[ProposalDecisionStatus["Executed"] = 4] = "Executed";
    ProposalDecisionStatus[ProposalDecisionStatus["Adopted"] = 5] = "Adopted";
})(ProposalDecisionStatus || (ProposalDecisionStatus = {}));
export var ProposalRewardStatus;
(function (ProposalRewardStatus) {
    ProposalRewardStatus[ProposalRewardStatus["Unspecified"] = 0] = "Unspecified";
    ProposalRewardStatus[ProposalRewardStatus["AcceptVotes"] = 1] = "AcceptVotes";
    ProposalRewardStatus[ProposalRewardStatus["ReadyToSettle"] = 2] = "ReadyToSettle";
    ProposalRewardStatus[ProposalRewardStatus["Settled"] = 3] = "Settled";
})(ProposalRewardStatus || (ProposalRewardStatus = {}));
export var NnsProposalTopic;
(function (NnsProposalTopic) {
    NnsProposalTopic[NnsProposalTopic["Unspecified"] = 0] = "Unspecified";
    NnsProposalTopic[NnsProposalTopic["NeuronManagement"] = 1] = "NeuronManagement";
    NnsProposalTopic[NnsProposalTopic["ExchangeRate"] = 2] = "ExchangeRate";
    NnsProposalTopic[NnsProposalTopic["NetworkEconomics"] = 3] = "NetworkEconomics";
    NnsProposalTopic[NnsProposalTopic["Governance"] = 4] = "Governance";
    NnsProposalTopic[NnsProposalTopic["NodeAdmin"] = 5] = "NodeAdmin";
    NnsProposalTopic[NnsProposalTopic["ParticipantManagement"] = 6] = "ParticipantManagement";
    NnsProposalTopic[NnsProposalTopic["SubnetManagement"] = 7] = "SubnetManagement";
    NnsProposalTopic[NnsProposalTopic["NetworkCanisterManagement"] = 8] = "NetworkCanisterManagement";
    NnsProposalTopic[NnsProposalTopic["KYC"] = 9] = "KYC";
    NnsProposalTopic[NnsProposalTopic["NodeProviderRewards"] = 10] = "NodeProviderRewards";
    NnsProposalTopic[NnsProposalTopic["SnsDecentralizationSale"] = 11] = "SnsDecentralizationSale";
    NnsProposalTopic[NnsProposalTopic["SubnetReplicaVersionManagement"] = 12] = "SubnetReplicaVersionManagement";
    NnsProposalTopic[NnsProposalTopic["ReplicaVersionManagement"] = 13] = "ReplicaVersionManagement";
    NnsProposalTopic[NnsProposalTopic["SnsAndCommunityFund"] = 14] = "SnsAndCommunityFund";
    NnsProposalTopic[NnsProposalTopic["ApiBoundaryNodeManagement"] = 15] = "ApiBoundaryNodeManagement";
    NnsProposalTopic[NnsProposalTopic["SubnetRental"] = 16] = "SubnetRental";
    NnsProposalTopic[NnsProposalTopic["ProtocolCanisterManagement"] = 17] = "ProtocolCanisterManagement";
    NnsProposalTopic[NnsProposalTopic["ServiceNervousSystemManagement"] = 18] = "ServiceNervousSystemManagement";
})(NnsProposalTopic || (NnsProposalTopic = {}));
export const ImageContentSchema = Type.Intersect([
    DataContentSchema,
    Type.Object({
        kind: Type.Literal("image_content"),
        height: Type.Number(),
        width: Type.Number(),
        thumbnailData: Type.String(),
        caption: Type.Optional(Type.String()),
        mimeType: Type.String(),
    }),
]);
export const MemeFighterContentSchema = Type.Object({
    kind: Type.Literal("meme_fighter_content"),
    height: Type.Number(),
    width: Type.Number(),
    url: Type.String(),
});
// Result card for a solved daily puzzle. `layout` is the hex-encoded puzzle description bytes
// (version, width, height, cells) so the card can draw the grid without fetching the puzzle.
export const DailyResultContentSchema = Type.Object({
    kind: Type.Literal("daily_result"),
    gameId: Type.String(),
    number: Type.Number(),
    userId: Type.String(),
    solveTimeMs: Type.Number(),
    hintsUsed: Type.Number(),
    streak: Type.Number(),
    layout: Type.String(),
    tier: Type.Optional(Type.Number()),
    caption: Type.Optional(Type.String()),
});
export const VideoContentSchema = Type.Object({
    kind: Type.Literal("video_content"),
    height: Type.Number(),
    width: Type.Number(),
    thumbnailData: Type.String(),
    caption: Type.Optional(Type.String()),
    mimeType: Type.String(),
    imageData: DataContentSchema,
    videoData: DataContentSchema,
    // Draft only: SHA3-256 of the file the user picked when videoData is a client-side
    // transcode of it, sent with the upload so CSAM checks also cover the original bytes
    sourceHash: Type.Optional(Type.Uint8Array()),
});
export const AudioContentSchema = Type.Intersect([
    DataContentSchema,
    Type.Object({
        kind: Type.Literal("audio_content"),
        caption: Type.Optional(Type.String()),
        mimeType: Type.String(),
        samples: Type.Uint8Array(),
        durationMs: Type.BigInt(),
    }),
]);
export const DeletedContentSchema = Type.Object({
    kind: Type.Literal("deleted_content"),
    deletedBy: Type.String(),
    timestamp: Type.BigInt(),
});
export const BlockedContentSchema = Type.Object({
    kind: Type.Literal("blocked_content"),
});
// Mirrors the category bits of `ModerationCategories` in the rust backend
export const MODERATION_CATEGORY_NAMES = [
    [1, "sexual"],
    [2, "sexual/minors"],
    [4, "violence"],
    [8, "violence/graphic"],
    [16, "harassment"],
    [32, "harassment/threatening"],
    [64, "self-harm"],
    [128, "illicit"],
];
// Synthesised client-side in the app store build for messages with moderation flags
export const RestrictedContentSchema = Type.Object({
    kind: Type.Literal("restricted_content"),
});
export const PollConfigSchema = Type.Object({
    allowMultipleVotesPerUser: Type.Boolean(),
    allowUserToChangeVote: Type.Boolean(),
    text: Type.Optional(Type.String()),
    showVotesBeforeEndDate: Type.Boolean(),
    endDate: Type.Optional(Type.BigInt()),
    anonymous: Type.Boolean(),
    options: Type.Array(Type.String()),
});
const AnonymousPollVotesSchema = Type.Object({
    kind: Type.Literal("anonymous_poll_votes"),
    votes: Type.Record(Type.Number(), Type.Number()),
});
const VisiblePollVotesSchema = Type.Object({
    kind: Type.Literal("visible_poll_votes"),
    votes: Type.Record(Type.Number(), Type.Array(Type.String())),
});
const HiddenPollVotesSchema = Type.Object({
    kind: Type.Literal("hidden_poll_votes"),
    votes: Type.Number(),
});
const TotalPollVotesSchema = Type.Union([
    AnonymousPollVotesSchema,
    VisiblePollVotesSchema,
    HiddenPollVotesSchema,
]);
export const PollVotesSchema = Type.Object({
    total: TotalPollVotesSchema,
    user: Type.Array(Type.Number()),
});
export const PollContentSchema = Type.Object({
    kind: Type.Literal("poll_content"),
    votes: PollVotesSchema,
    config: PollConfigSchema,
    ended: Type.Boolean(),
});
export const TextContentSchema = Type.Object({
    kind: Type.Literal("text_content"),
    text: Type.String(),
});
export const FileContentSchema = Type.Intersect([
    DataContentSchema,
    Type.Object({
        kind: Type.Literal("file_content"),
        name: Type.String(),
        caption: Type.Optional(Type.String()),
        mimeType: Type.String(),
        fileSize: Type.Number(),
    }),
]);
const MessageContextSchema = Type.Object({
    chatId: ChatIdentifierSchema,
    threadRootMessageIndex: Type.Optional(Type.Number()),
});
export function messageContextFromString(ctxStr) {
    return JSON.parse(ctxStr);
}
export function messageContextToString(ctx) {
    return JSON.stringify(ctx);
}
export function messageContextToChatListScope(ctx) {
    switch (ctx.chatId.kind) {
        case "channel":
            return {
                kind: "community",
                id: { kind: "community", communityId: ctx.chatId.communityId },
            };
        case "direct_chat":
        case "group_chat":
            return { kind: "chats" };
    }
}
const RawReplyContextSchema = Type.Object({
    kind: Type.Literal("raw_reply_context"),
    eventIndex: Type.Number(),
    sourceContext: Type.Optional(MessageContextSchema),
});
export function isSuccessfulEventsResponse(response) {
    return response !== undefined && typeof response === "object" && "events" in response;
}
export function assertSuccessfulEventsResponse(response) {
    if (!isSuccessfulEventsResponse(response)) {
        throw new Error("Events response error: " + JSON.stringify(response));
    }
}
export function messageContextsEqual(a, b) {
    if (a === undefined && b === undefined) {
        return true;
    }
    if (a === undefined || b === undefined) {
        return false;
    }
    return (chatIdentifiersEqual(a.chatId, b.chatId) &&
        a.threadRootMessageIndex === b.threadRootMessageIndex);
}
export function chatIdentifierUnset(id) {
    if (id === undefined)
        return true;
    switch (id.kind) {
        case "channel":
            return id.channelId === 0;
        case "direct_chat":
            return id.userId === "";
        case "group_chat":
            return id.groupId === "";
    }
}
export function chatIdentifiersEqual(a, b) {
    if (a === undefined && b === undefined) {
        return true;
    }
    if (a === undefined || b === undefined) {
        return false;
    }
    if (a.kind !== b.kind) {
        return false;
    }
    switch (a.kind) {
        case "channel":
            return (b.kind === "channel" &&
                a.communityId === b.communityId &&
                a.channelId === b.channelId);
        case "direct_chat":
            return b.kind === "direct_chat" && a.userId === b.userId;
        case "group_chat":
            return b.kind === "group_chat" && a.groupId === b.groupId;
    }
}
export function nullMembership() {
    return {
        joined: BigInt(0),
        role: ROLE_NONE,
        mentions: [],
        latestThreads: [],
        myMetrics: emptyChatMetrics(),
        notificationsMuted: false,
        atEveryoneMuted: false,
        readByMeUpTo: undefined,
        archived: false,
        rulesAccepted: false,
        lapsed: false,
    };
}
export function emptyEventsResponse() {
    return {
        events: [],
        expiredEventRanges: [],
        latestEventIndex: undefined,
    };
}
